import React, { useEffect, useReducer, useState } from 'react';
import ConnectedTvIcon from '@mui/icons-material/ConnectedTv';
import RouterIcon from '@mui/icons-material/Router';
import {
  addAssignee,
  addEntry,
  addResolution,
  createContact,
  createWorkOrderPdf,
  deleteResolution,
  searchResolutions,
  searchTags,
  updateContact,
  updateResolution,
  updateTicket,
  updateTicketTags,
  verifySecurityTicketTag,
} from 'shared/api/support';
import { CardControls, CardSection } from 'shared/components/cardControls';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { compareArrayOfObjects, isBlankString } from 'shared/utils';
import { MenuButton } from 'shared/components/MenuButton';

import { DataReducerActions } from 'shared/reducers';
import { isManagerOf } from 'shared/api/auth';
import { hasValidContactInformationUpdates } from './subcomponents/ContactInformation';
import TicketInfo from './TicketInfo';
import { compileSearchString, compileUpdatedSearchString } from './subcomponents/TicketResolver';
import { TICKET_STATUS } from '../../shared/constants/support';

/**
 * @param {Object} props
 * @param {Function} props.ticketDispatch
 * @param {Object} props.ticketState
 */
export default function TicketView(props) {
  const {
    ticketDispatch,
    ticketState: {
      data,
      dirty,
      editEnabled,
      updatedData,
    },
  } = props;

  const { user } = useAuth();
  const {
    request,
    isLoading,
  } = useRequest();
  const [isManager, setIsManager] = useState(false);
  const notification = useNotification();

  const initialState = {
    initialTicketTags: [],
    stagedTicketTags: [],
    disableOptions: false,
  };

  const ticketReducer = (state, action) => {
    switch (action.type) {
      case 'INITIAL_TAGS':
        return {
          ...state,
          initialTicketTags: action.payload,
          stagedTicketTags: action.payload,
        };
      case 'STAGE_TAGS':
        if (action.payload.length > 4) {
          return {
            ...state,
            stagedTicketTags: action.payload,
            disableOptions: true,
          };
        }
        return {
          ...state,
          stagedTicketTags: action.payload,
          disableOptions: false,
        };
      default:
        return state;
    }
  };
  const [ticketTagData, setTicketTagData] = useReducer(ticketReducer, initialState, undefined);

  const [resolutionCount, setResolutionCount] = useState(0);

  useEffect(() => {
    if (data?.status === 'Resolved' || updatedData?.status === 'Resolved') {
      (async () => {
        let search;
        if (updatedData.staged_ticket_tags) {
          if (updatedData.staged_ticket_tags.length === 0) {
            setResolutionCount(0);
            return;
          }
          search = `?${compileUpdatedSearchString(updatedData.staged_ticket_tags)}`;
        } else {
          search = `?${compileSearchString(data.ticket_tags)}`;
        }

        const results = await request(searchResolutions(search));
        if (results) {
          setResolutionCount(results.length);
        }
      })();
    }
  }, [
    updatedData?.status,
    setResolutionCount,
    request,
    data?.status,
    data.ticket_tags,
    updatedData.staged_ticket_tags,
  ]);
  const [isSecurityTicketTag, setIsSecurityTicketTag] = useState(false);
  const setStagedTicketTagsCall = (tagsArray) => {
    setTicketTagData({
      type: 'STAGE_TAGS',
      payload: tagsArray,
    });
    ticketDispatch({
      action: 'updateArray',
      payload: {
        key: 'staged_ticket_tags',
        value: tagsArray,
      },
    });
  };

  useEffect(() => {
    (
      async () => {
        let response;
        if (updatedData.staged_ticket_tags) {
          response = await Promise
            .all(updatedData.staged_ticket_tags.map(
              (tag) => request(verifySecurityTicketTag(tag.id)),
            ));
        } else {
          response = await Promise
            .all(data.ticket_tags.map((tag) => request(verifySecurityTicketTag(tag.tag_id))));
        }
        setIsSecurityTicketTag(response.some((e) => e.is_security_type === true));
      }
    )();
  }, [updatedData.staged_ticket_tags, data.ticket_tags, request]);

  // Set initial tags from the ticket_tags in the data
  useEffect(() => {
    const initialResultTags = data.ticket_tags;
    if (initialResultTags.length === 0) {
      return;
    }
    (async () => {
      const result = await request(searchTags(''), '');
      if (!result) {
        return;
      }
      const initialTags = result.rows.filter(
        (tag) => initialResultTags.some((ticketTag) => tag.id === ticketTag.tag_id),
      )
        .map(
          (it) => ({
            id: it.id,
            name: it.name,
          }),
        );

      setTicketTagData({
        type: 'INITIAL_TAGS',
        payload: initialTags,
      });
      ticketDispatch({
        action: 'addData',
        payload: {
          name: 'staged_ticket_tags',
          value: initialTags,
        },
      });
    })();
  }, [request, data.ticket_tags, ticketDispatch]);

  // If the ticket tags are changed
  // then match the staged ticket tags against the available ticket tags

  useEffect(() => {
    const stagedTags = updatedData.staged_ticket_tags || data.staged_ticket_tags;
    if (!stagedTags) {
      return;
    }

    (async () => {
      const result = await request(searchTags(''), '');
      if (!result) {
        return;
      }

      const resultTags = result.rows.filter(
        (tag) => stagedTags.some((ticketTag) => tag.id === ticketTag.id),
      )
        .map(
          (it) => ({
            id: it.id,
            name: it.name,
          }),
        );
      setTicketTagData({
        type: 'STAGE_TAGS',
        payload: resultTags,
      });
    })();
  }, [
    request,
    updatedData.staged_ticket_tags,
    data.staged_ticket_tags,
  ]);

  useEffect(() => {
    (async () => {
      const result = await request(isManagerOf(user.id, data.assignee_id));
      if (!result) {
        return;
      }
      const isManagerResult = result === 'true';
      setIsManager(isManagerResult);
    })();
  }, [data.assignee_id, request, user.id]);

  function validateData() {
    // Track return value to validate everything in one pass
    let isValid = true;
    if (!ticketTagData.stagedTicketTags
      || ticketTagData.stagedTicketTags.length > 5
      || ticketTagData.stagedTicketTags.length === 0) {
      notification.pushError('Must select between 1 - 5 tags');
      isValid = false;
    }

    if (!updatedData.assignee_id && !data.assignee_id) {
      notification.pushError('Requires a valid assignee');
      isValid = false;
    }

    if ((updatedData.text && !updatedData.minutes) || (updatedData.minutes && !updatedData.text)) {
      notification.pushError('New entries require both new description and minutes');
      isValid = false;
    }

    if (
      ((data.status === TICKET_STATUS.resolved
          && updatedData?.status !== TICKET_STATUS.active)
        || updatedData?.status === TICKET_STATUS.resolved)
      && resolutionCount > 0
      && (!updatedData.resolution_id && !data.ticket_resolution?.id)) {
      notification.pushError('Requires a resolution');
      isValid = false;
    }

    if (updatedData.title && isBlankString(updatedData.title)) {
      notification.pushError('Requires a valid title');
      isValid = false;
    }

    // Check if contact info is valid
    if (isSecurityTicketTag) {
      const contactsValid = hasValidContactInformationUpdates(data, updatedData);
      if (!contactsValid) {
        notification.pushError('Requires a valid contact name and either a valid security contact phone number or email address');
        isValid = false;
      }
    }
    return isValid;
  }

  const saveChanges = async () => {
    if (!validateData()) {
      return;
    }

    const promiseArr = [];
    // Ensure ticket_id is set
    updatedData.ticket_id = data.id;
    if (updatedData.name || 'email' in updatedData || 'phone' in updatedData) {
      if (data?.ticket_contact?.id) {
        // if a security contact already exists update the security contact
        const contactUpdate = updatedData?.name
          ? updatedData
          : {
            ...updatedData,
            name: data.ticket_contact.name,
          };
        promiseArr.push(request(updateContact(data.ticket_contact.id, contactUpdate), 'Updated security contact'));
      } else {
        // else create a new security contact
        promiseArr.push(request(createContact(updatedData), 'Created security contact'));
      }
    }
    if (updatedData.text) {
      if (data.status === TICKET_STATUS.resolved) {
        // Reactivate a resolved ticket if there are further entries
        updatedData.status = TICKET_STATUS.active;
      }
      promiseArr.push(request(addEntry({
        ...updatedData,
        username: user.username,
      }), 'Updated entry'));
    }
    if (updatedData.resolution_id) {
      if (data.ticket_resolution?.resolution_id
        && data.ticket_resolution?.resolution_id !== updatedData.resolution_id) {
        // if there is an existing ticket resolution, update the resolution with new resolution id
        promiseArr.push(request(updateResolution(
          data.id,
          {
            id: data.ticket_resolution.id,
            resolution_id: updatedData.resolution_id,
          },
        ), 'Updated resolution'));
      } else {
        // else create resolution
        promiseArr.push(request(addResolution(data.id, updatedData), 'Updated resolution'));
      }
    }
    if (updatedData.status === TICKET_STATUS.active
      && data.status === TICKET_STATUS.resolved
      && data.ticket_resolution?.id) {
      // if there is an existing ticket resolution and the status is being updated to active
      // delete the ticket resolution
      promiseArr.push(request(deleteResolution(data.id), 'Deleted resolution'));
      updatedData.ticket_resolution = null;
    }
    if (updatedData.assignee_id) {
      promiseArr.push(request(addAssignee({
        ...updatedData,
        assigner_id: user.id,
        assigner_username: user.username,
      }), 'Updated assignee'));
    }
    if (
      updatedData.priority
      || updatedData.status
      || updatedData.title
    ) {
      promiseArr.push(request(updateTicket(data.id, {
        ...updatedData,
        last_updated_by: user.username,
      }), 'Updated ticket'));
    }
    const simplifiedTicketTags = data.ticket_tags.map(
      (tt) => ({
        id: tt.tag.id,
        name: tt.tag.name,
      }),
    );
    if (!compareArrayOfObjects(ticketTagData.stagedTicketTags, simplifiedTicketTags)) {
      promiseArr.push(request(updateTicketTags(data.id, {
        username: user.username,
        tag_ids: ticketTagData.stagedTicketTags.map((stt) => (stt).id),
      }), 'Updated ticket tags'));
    }

    if (promiseArr.length === 0) return;

    const response = await Promise
      .all(promiseArr)
      .catch(() => notification.pushError('Failed to update ticket'));

    if (response) {
      // if update successful, retrieve new ticket data
      ticketDispatch({ action: 'reload' });
    }
  };

  const handleChange = (key, value) => ticketDispatch({
    action: DataReducerActions.update,
    payload: {
      key,
      value,
    },
  });

  const handleReload = () => ticketDispatch({ action: 'reload' });

  const downloadInternetWOPDF = async () => {
    await request(createWorkOrderPdf('internet', data.id));
  };

  const downloadDTVWOPDF = async () => {
    await request(createWorkOrderPdf('satellite', data.id));
  };

  const validateTagLength = () => {
    if (!ticketTagData.stagedTicketTags
      || ticketTagData.stagedTicketTags.length > 5
      || ticketTagData.stagedTicketTags.length === 0) {
      notification.pushError('Must select between 1 - 5 tags');
    }
  };

  const workOrderItems = [
    {
      onClickFunc: downloadInternetWOPDF,
      title: 'Internet',
      icon: <RouterIcon />,
    },
    {
      onClickFunc: downloadDTVWOPDF,
      title: 'Satellite',
      icon: <ConnectedTvIcon />,
    },
  ];

  const workOrderButton = (
    <MenuButton
      title="Work Orders"
      buttonVariant="outlined"
      menuItems={workOrderItems}
    />
  );

  return (
    <CardSection editEnabled={editEnabled} saveChanges={saveChanges}>
      <CardControls
        clearChanges={() => {
          ticketDispatch({ action: DataReducerActions.clear });
          setTicketTagData({
            type: 'STAGE_TAGS',
            payload: ticketTagData.initialTicketTags,
          });
        }}
        disabled={isLoading}
        dirty={dirty}
        editEnabled={editEnabled}
      >
        {workOrderButton}
      </CardControls>
      <TicketInfo
        handleChange={handleChange}
        data={data}
        updatedData={updatedData}
        showResolutionTypes={resolutionCount > 0}
        handleReload={handleReload}
        stagedTicketTags={ticketTagData.stagedTicketTags}
        setStagedTicketTags={setStagedTicketTagsCall}
        validateTagLength={validateTagLength}
        disableOptions={ticketTagData.disableOptions}
        isManager={isManager}
      />
    </CardSection>
  );
}
